<template>
  <v-container>
    <v-progress-circular
      v-if="loading"
      :indeterminate="true"
    />
    <v-row v-else>
      <v-col
        cols="12"
        :xl="productUid ? '6' : '12'"
      >
        <v-container>
          <v-row>
            <v-col md="12">
              <base-material-card
                color="success"
                :title="productTitle"
              >
                <v-form ref="form">
                  <v-container class="py-0">
                    <v-row>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-text-field
                          v-model="product.name"
                          :label="$t('Components.Forms.ProductDetails.Labels.ProductName')"
                          :rules="[nameValidation]"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <vendorDropdown
                          v-model="product.vendorId"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-autocomplete
                          v-model="product.contentNodeId"
                          color="primary"
                          item-color="primary"
                          :label="$t('Components.Forms.ProductDetails.Labels.ContentNode')"
                          :items="contentNodes"
                          item-text="name"
                          item-value="nodeId"
                          :rules="[contentNodeIdValidation]"
                        />
                      </v-col>
                      <!-- <v-col
                        cols="12"
                        md="6"
                      >
                        <v-text-field
                          v-model="product.availableLanguageCodes"
                          label="Available language codes"
                        />
                      </v-col> -->
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-text-field
                          v-model.number="product.daysTrial"
                          :label="$t('Components.Forms.ProductDetails.Labels.TrialDays')"
                          type="number"
                          :rules="[daysTrialValidation]"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-checkbox
                          v-model="product.coTerminationAllowed"
                          :label="$t('Components.Forms.ProductDetails.Labels.CoTerminationAllowed')"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-autocomplete
                          v-model="product.displayTemplateAlias"
                          color="primary"
                          item-color="primary"
                          :label="$t('Components.Forms.ProductDetails.Labels.DisplayTemplateAlias')"
                          :items="displayTemplateAliases"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        md="6"
                      >
                        <v-checkbox
                          v-model="product.published"
                          :label="$t('Components.Forms.ProductDetails.Labels.Published')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-form>
                <template v-slot:actions>
                  <v-row
                    no-gutters
                    align="center"
                  >
                    <v-col
                      cols="auto"
                      class="mr-auto mb-3 mb-md-0 d-block d-sm-inline-block"
                    >
                      <v-icon class="mr-1">
                        mdi-menu-left
                      </v-icon>
                      <router-link
                        :to="{ name: 'ManageProducts', }"
                      >
                        <span>{{ $t('General.Links.All', ['products']) }}</span>
                      </router-link>
                    </v-col>
                    <v-col cols="auto">
                      <debounce-v-btn
                        class="font-weight-light"
                        color="success"
                        loading:loading
                        @click="Save"
                      >
                        {{ $t('General.Buttons.Save') }}
                      </debounce-v-btn>
                      <debounce-v-btn
                        v-if="!isNewProduct"
                        class="font-weight-light"
                        color="success"
                        loading:loading
                        @click="SaveAndClose"
                      >
                        {{ $t('General.Buttons.SaveClose') }}
                      </debounce-v-btn>
                      <debounce-v-btn
                        v-if="isNewProduct"
                        class="font-weight-light v-btn--tight"
                        color="warning"
                        loading:loading
                        @click="Close"
                      >
                        {{ $t('General.Buttons.DiscardChanges') }}
                      </debounce-v-btn>
                      <v-btn
                        v-if="!isNewProduct"
                        class="font-weight-light v-btn--tight"
                        color="error"
                        loading:loading
                        @click="resetProduct"
                      >
                        {{ $t('General.Buttons.ResetChanges') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </template>
              </base-material-card>
            </v-col>
          </v-row>
        </v-container>
      </v-col>

      <v-col
        v-if="productUid"
        cols="12"
        xl="6"
      >
        <v-container>
          <v-row>
            <pricingTableEditable
              :product-uid="productUid"
            />
          </v-row>
        </v-container>
      </v-col>

      <v-col
        v-if="product.uid"
        cols="12"
        xl="12"
      >
        <v-container>
          <v-row>
            <v-col md="12">
              <base-material-card
                color="success"
                icon="mdi-view-grid-outline"
                inline
                class="px-5 py-3"
                top-actions
              >
                <template v-slot:after-heading>
                  <div class="display-2 font-weight-light">
                    {{ $t('Variants') }}
                  </div>
                </template>

                <template v-slot:top-actions>
                  <v-btn
                    class="px-2 ml-1"
                    color="success"
                    @click="gotoVariant()"
                  >
                    {{ $t('General.Buttons.New', ['variant']) }}
                  </v-btn>
                  <v-btn
                    class="px-2 ml-1 v-btn--tight"
                    color="info"
                    @click="refreshVariants()"
                  >
                    {{ $t('General.Buttons.Refresh') }}
                  </v-btn>
                </template>

                <v-divider class="mt-3" />

                <v-data-table
                  :headers="variantHeaders"
                  hide-default-header
                  :items="product.variants"
                  :sort-desc="[false]"
                  multi-sort
                >
                  <template v-slot:header="{ props: { headers } }">
                    <thead>
                      <tr>
                        <th
                          v-for="(header, i) in headers"
                          :key="`header-${i}`"
                        >
                          {{ $t(header.text) }}
                        </th>
                      </tr>
                    </thead>
                  </template>
                  <template v-slot:item="{ item }">
                    <tr>
                      <td>
                        <v-btn
                          class="px-2 ml-1"
                          color="warning"
                          rounded
                          small
                          @click="gotoVariant(item)"
                        >
                          {{ $t('General.Buttons.Edit') }}
                        </v-btn>
                      </td>
                      <td>{{ item.name }}</td>
                      <td>{{ item.externalSku }}</td>
                      <td>{{ item.minimumOrderAmount }}</td>
                      <td>{{ item.recurring ? item.recurringIntervalQuantity + ' ' + getInterval(item.recurringTimeInterval, item.recurringIntervalQuantity) : $t('General.Labels.No') }}</td>
                      <td>€{{ item.msrp ? item.msrp.toFixed(2) : 0 }}</td>
                      <td>{{ item.published ? $t('General.Labels.Yes') : $t('General.Labels.No') }}</td>
                      <td>
                        <v-btn
                          v-if="product.variants.length > 1"
                          class="px-2 ml-1"
                          color="error"
                          rounded
                          small
                          @click="showDeleteProductVariantDialog(item)"
                        >
                          {{ $t('General.Buttons.Delete') }}
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </base-material-card>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-dialog
      v-model="deleteProductVariantDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          {{ $t('Components.Dialogs.DeleteProductVariant.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="cancelProductVariantDeletion()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text class="text-center font-weight-bold">
          <p>{{ $t('Components.Dialogs.DeleteProductVariant.Text', [deleteProductVariantMessage]) }}</p>
        </v-card-text>
        <v-card-text class="pb-6 pt-12 text-center">
          <v-btn
            class="mr-3"
            text
            @click="cancelProductVariantDeletion()"
          >
            {{ $t('General.Buttons.Cancel') }}
          </v-btn>

          <debounce-v-btn
            color="error"
            text
            @click="deleteProductVariant()"
          >
            {{ $t('General.Buttons.Delete') }}
          </debounce-v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import productContentService from '@/services/productContentService'
  import vendorDropdown from '../../views/dashboard/components/dropdowns/Vendor.vue'
  import pricingTableEditable from '../components/custom/PricingTableEditable.vue'
  import variantService from '@/services/Admin/variantService'
  import validation from '@/helpers/validationHelper'

  export default {
    components: {
      vendorDropdown,
      pricingTableEditable,
    },
    props: {
      productUid: {
        type: String,
        required: false,
        default: null,
      },
    },
    data: () => ({
      product: {
        uid: '',
        name: '',
        vendorId: '',
        contentNodeId: '',
        published: false,
        // availableLanguageCodes: '',
        daysTrial: 0,
        coterminationAllowed: false,
        displayTemplateAlias: '',
        variants: [],
      },
      loading: true,
      contentNodes: [],
      displayTemplateAliases: [],
      timeIntervals: [],
      variantHeaders: [
        {
          sortable: false,
          text: '',
          width: '1%',
        },
        {
          sortable: true,
          text: 'Pages.ManageProduct.TableHeaders.VariantName',
          value: 'displayName',
        },
        {
          sortable: true,
          text: 'Pages.ManageProduct.TableHeaders.Sku',
          value: 'sku',
        },
        {
          sortable: true,
          text: 'Pages.ManageProduct.TableHeaders.MinimumOrder',
          value: 'minimumOrderQuantity',
        },
        {
          sortable: true,
          text: 'Pages.ManageProduct.TableHeaders.Recurring',
          value: 'recurring',
        },
        {
          sortable: true,
          text: 'Pages.ManageProduct.TableHeaders.CustomerPrice',
          value: 'price',
        },
        {
          sortable: true,
          text: 'Pages.ManageProduct.TableHeaders.Published',
          value: 'published',
        },
        {
          sortable: false,
          text: '',
          width: '1%',
        },
      ],
      productVariantToDelete: null,
      deleteProductVariantDialog: false,
    }),
    computed: {
      products () {
        return this.$store.state.productsAdmin.adminList
      },
      productTitle () {
        return this.productUid && this.product ? this.$t('Components.Forms.ProductDetails.Titles.ExistingProductTitle', [this.product.name]) : this.$t('Components.Forms.ProductDetails.Titles.NewProductTitle')
      },
      isNewProduct () {
        return !this.productUid
      },
      deleteProductVariantMessage () {
        return this.productVariantToDelete ? this.$t('Components.Dialogs.DeleteProductVariant.DeleteProductVariantMessage', [this.productVariantToDelete.name]) : null
      },
    },
    created: async function () {
      if (this.$store.state.productsAdmin.adminList.length === 0) {
        await this.$store.dispatch('RefreshAdminProducts', true)
      }

      var contentNodesApiResult = await productContentService.getOverview()
      if (contentNodesApiResult.success) {
        this.contentNodes = contentNodesApiResult.value
      }
      var intervalsApiResult = await variantService.getTimeIntervals()
      if (intervalsApiResult.success) {
        this.timeIntervals = intervalsApiResult.value
      }
      this.displayTemplateAliases = ['', 'secureDns']

      if (!this.productUid) {
        this.loading = false
        return
      }

      await this.getProductDetails()
      this.loading = false
    },
    beforeMount () { },
    methods: {
      openLink: function (url) {
        window.open(url, '_blank')
      },
      async resetProduct () {
        await this.getProductDetails()
      },
      refreshVariants () {
        this.$store.dispatch('RefreshAdminProducts')
      },
      async Save () {
        if (this.$refs.form.validate() !== true) {
          return false
        }

        this.loading = true
        var eventType = this.product.uid ? 'UpdateProduct' : 'CreateProduct'
        var storeChange = await this.$store.dispatch(eventType, this.product)

        if (storeChange.success === false) {
          this.loading = false
          return false
        }

        this.updateLocalProduct(storeChange.value)
        if (!this.productUid) {
          this.$router.push({
            name: 'ManageProductVariant',
            params: { productUid: storeChange.value.uid, isNewProduct: true, variantUid: storeChange.value.variants[0].uid },
          })
        }
        this.loading = false
        return true
      },
      async SaveAndClose () {
        if (await this.Save()) {
          this.Close()
        }
      },
      Close () {
        this.$router.go(-1)
      },
      gotoVariant (variant) {
        this.$router.push({
          name: 'ManageProductVariant',
          params: { productUid: this.productUid, variantUid: variant ? variant.uid : null },
        })
      },
      async getProductDetails () {
        var currentProd = this.products.find(
          el => el.uid === this.productUid
        )
        if (!currentProd) {
          await this.$store.dispatch('RefreshAdminProducts')
          currentProd = this.products.find(
            el => el.uid === this.productUid
          )
          if (!currentProd) {
            console.log('[Unable to find product by UID]')
            return
          }
        }
        this.updateLocalProduct(currentProd)
      },
      updateLocalProduct (data) {
        this.product.uid = data.uid
        this.product.name = data.name
        this.product.vendorId = data.vendorId
        this.product.contentNodeId = data.contentNodeId
        this.product.published = !!data.published
        this.product.variants = data.variants
        // this.product.availableLanguageCodes = data.availableLanguageCodes
        this.product.daysTrial = data.daysTrial
        this.product.coterminationAllowed = data.coterminationAllowed
        this.product.displayTemplateAlias = data.displayTemplateAlias
      },
      getInterval (interval, quantity) {
        var matchingTimeUnit = this.timeIntervals.find(el => el.value === interval)
        if (matchingTimeUnit) {
          return matchingTimeUnit.unitText + (parseInt(quantity) > 1 ? 's' : '')
        }
        return ''
      },
      showDeleteProductVariantDialog (item) {
        this.productVariantToDelete = item
        this.productVariantToDelete.productUid = this.productUid
        this.deleteProductVariantDialog = true
      },
      async deleteProductVariant () {
        await this.$store.dispatch('DeleteProductVariant', this.productVariantToDelete)
        this.getProductDetails()
        this.productVariantToDelete = null
        this.deleteProductVariantDialog = false
      },
      cancelProductVariantDeletion () {
        this.productVariantToDelete = null
        this.deleteProductVariantDialog = false
      },
      // validation
      nameValidation (value) {
        return validation.stringIsNotNullOrWhitespace(value) || this.$t('Components.Forms.ProductDetails.Validation.NameRequired')
      },
      contentNodeIdValidation (value) {
        return value > 0 || this.$t('Components.Forms.ProductDetails.Validation.ContentNodeRequired')
      },
      daysTrialValidation (value) {
        return validation.numberIsNotNegative(value) || this.$t('Components.Forms.ProductDetails.Validation.TrialDaysGreaterThanZero')
      },
    },
  }
</script>

<style>
  td.link{cursor:pointer}
</style>
